import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import { useIsMobile } from '@/hooks/use-mobile';
import AppLayout from '@/layouts/app-layout';
import { type BreadcrumbItem } from '@/types';
import { type ReactNode, useState } from 'react';

interface ShowLayoutProps {
    children: ReactNode;
    sidebar?: ReactNode;
    breadcrumbs?: BreadcrumbItem[];
    title?: string | ReactNode;
    mobileSidebarTitle?: string | ReactNode;
    sidebarOpen?: boolean;
    onSidebarOpenChange?: (open: boolean) => void;
}

export default function ShowLayout({
    children,
    sidebar,
    breadcrumbs,
    title,
    mobileSidebarTitle,
    sidebarOpen: sidebarOpenProp,
    onSidebarOpenChange,
}: ShowLayoutProps) {
    const [internalSidebarOpen, setInternalSidebarOpen] = useState(false);
    const isMobile = useIsMobile();

    // Use controlled sidebar state if provided, otherwise use internal state
    const isSidebarOpen = sidebarOpenProp !== undefined ? sidebarOpenProp : internalSidebarOpen;
    const setIsSidebarOpen = onSidebarOpenChange || setInternalSidebarOpen;

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <div className="space-y-6 p-4 sm:p-6 lg:p-8">
                {title && (
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
                    </div>
                )}

                {sidebar ? (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
                        <div className="space-y-6 lg:col-span-2">{children}</div>
                        {!isMobile && (
                            <div className="lg:sticky lg:top-6 lg:col-span-1 lg:self-start">
                                <div className="space-y-6">{sidebar}</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">{children}</div>
                )}

                {/* Mobile Bottom Sheet */}
                {sidebar && isMobile && (
                    <Drawer open={isSidebarOpen} onOpenChange={setIsSidebarOpen} direction="bottom">
                        <DrawerContent className="max-h-[85vh] rounded-t-2xl">
                            <DrawerHeader className="pt-4 pb-3">
                                <DrawerTitle className="text-center text-lg font-semibold">{mobileSidebarTitle ?? title ?? 'Details'}</DrawerTitle>
                            </DrawerHeader>
                            <div className="overflow-y-auto px-4 pb-6">{sidebar}</div>
                        </DrawerContent>
                    </Drawer>
                )}
            </div>
        </AppLayout>
    );
}
